import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Check, Minus } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button as UiButton } from "@/components/ui/button";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  Button,
  Footnote,
  Pill,
  SectionHeading,
  Sheet,
  SubHeading,
  plural,
  relativeDay,
} from "../components/kit";
import { TONE_CLASSES, TONE_EDGE } from "@shared/status";

/**
 * Who has counted this week — the enforcement screen.
 *
 * The client's chronic failure is not ordering, it is *checking*: people do not
 * do the weekly sweep, so the par-level model quietly collapses back into the
 * spreadsheet. v2's whole trigger chain hangs off a count actually being
 * submitted, which makes "which clinic has not counted" the one question this
 * screen exists to answer, in one glance, at 390px, before anything else is
 * read.
 *
 * So the current week leads and everything else is subordinate to it:
 *
 *   1. **One card per clinic, not-counted first.** The order is the answer —
 *      a reader who stops after the first card has still learned the thing
 *      they came for.
 *   2. **The status vocabulary is the client's own**, not a new one invented
 *      for this screen: gray is settled/done (the same gray a received line
 *      wears), red is "somebody still has to do this". No third colour, no
 *      amber — a missed count is not a qualifier, it is a state.
 *   3. **The history is a strip, not a chart.** Five cells per clinic says
 *      "this one misses every other week" faster than any figure, and SPEC's
 *      planned-never-installed list is binding: there is no chart library in
 *      this app and this screen is not the reason to add one.
 *
 * Owner-only, and gated on the server — `counts.compliance` calls
 * `requireOwner`. The route is owner-only too, but that is the belt, not the
 * braces.
 */

/** `"2026-W32"` → `"Week 32, 2026"`. The key is the client's ISO week, Mon–Sun
 *  (see `shared/week.ts`); nothing here re-derives it. */
function weekLabel(key: string): string {
  const [year, week] = key.split("-W");
  return week ? `Week ${week}, ${year}` : key;
}

/** The same key at strip width: `"W32"`. */
function weekShort(key: string): string {
  const week = key.split("-W")[1];
  return week ? `W${week}` : key;
}

export function Compliance() {
  // Remounting the boundary is how a failed query gets retried: Convex throws
  // during render, so there is nothing to re-run without a fresh subtree.
  const [attempt, setAttempt] = useState(0);
  return (
    <ScreenBoundary
      key={attempt}
      title="The count record could not load"
      heading={<SectionHeading title="Counts" />}
      onRetry={() => setAttempt((n) => n + 1)}
    >
      <ComplianceScreen />
    </ScreenBoundary>
  );
}

function ComplianceScreen() {
  /* `built()` in router.tsx declares its `path` as `string`, so the registered
     route literals are only "/" and "$" — a `Link to="/approvals"` does not
     typecheck while a `string` does. Same reason kit's `<Tile to>` is typed
     `string`. */
  const approvalsPath: string = "/approvals";
  /* The server's default window is five weeks — this week plus four of
     history, which is exactly what the strip below is sized for. */
  const data = useQuery(api.counts.compliance, {});
  const [openSession, setOpenSession] = useState<Id<"countSessions"> | null>(
    null,
  );

  if (data === undefined) {
    return (
      <div data-screen-state="loading">
        <Loading rows={5} label="Loading the count record" />
      </div>
    );
  }

  const { currentWeekKey, weekKeys, clinics } = data;

  /** This week's cell for a clinic. Found by key rather than taken off the end
   *  of the array: the payload's ordering is the server's business. */
  const thisWeek = (clinic: (typeof clinics)[number]) =>
    clinic.weeks.find((week) => week.weekKey === currentWeekKey) ?? null;

  /* Outstanding first, then alphabetical inside each half. The list order is
     the answer to the screen's question, so it is not the payload's
     alphabetical order — a reader who stops at the first card has still learned
     who still owes a count this week. A clinic that simply has not counted
     *yet* is outstanding but not a failure — it sorts up, it does not go red. */
  const current = [...clinics].sort((a, b) => {
    const done = Number(thisWeek(a)?.state === "counted");
    const other = Number(thisWeek(b)?.state === "counted");
    return done - other || a.clinicName.localeCompare(b.clinicName);
  });

  const countedThisWeek = clinics.filter(
    (clinic) => thisWeek(clinic)?.state === "counted",
  ).length;

  /* Newest first. `weekKeys` arrives oldest first — the strip reads
     right-to-left in time otherwise, which is the wrong way round for
     "has this clinic counted lately". */
  const history = weekKeys
    .filter((key) => key !== currentWeekKey)
    .slice()
    .reverse();

  const anyCountEver = clinics.some((clinic) =>
    clinic.weeks.some((week) => week.submitted),
  );

  if (clinics.length === 0) {
    return (
      <div data-screen-state="ready" className="space-y-4 pb-8">
        <SectionHeading title="Counts" />
        <Empty
          title="No clinics yet"
          body="Add a clinic and its weekly count appears here."
        />
      </div>
    );
  }

  return (
    <div data-screen-state="ready" className="space-y-5 pb-8">
      <SectionHeading
        title="Counts"
        subtitle={`${weekLabel(currentWeekKey)} · ${countedThisWeek} of ${clinics.length} ${plural(
          clinics.length,
          "clinic",
        )} counted`}
      />

      {/* The zero state, and it is not "nothing here". Five clinics that have
          never counted is a story about the rollout, not an empty list, so the
          cards below still render — all of them red — and this says why they
          all are. */}
      {anyCountEver ? null : (
        <Empty
          title="No counts recorded yet"
          body={`Nothing has been submitted in the last ${weekKeys.length} weeks. Every clinic below is still to count.`}
        />
      )}

      <section className="space-y-2 lg:grid lg:grid-cols-2 lg:items-start lg:gap-2 lg:space-y-0">
        {current.map((clinic) => {
          const week = thisWeek(clinic);
          const counted = week?.state === "counted";
          /* Two tones this week, and neither is red. Gray is done — the same
             settled gray a received line wears. Amber is "still owed this week":
             the current week is in progress, so an un-counted clinic is
             outstanding, not delinquent. Red is reserved for the history strip's
             genuinely-past misses, so a colour that fires every Monday morning
             does not train the owner to stop looking. */
          const tone = counted ? "gray" : "amber";
          return (
            <Card
              key={clinic.clinicId}
              /* The app's 4px left rule, in the card's tone. Stock card geometry
                 otherwise — `gap-0 p-3` is the shim over the header/content
                 slots this card does not use. */
              className={cn("gap-0 border-l-4 p-3", TONE_EDGE[tone])}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                  {clinic.clinicName}
                </span>
                {/* Gray is done; amber is "not yet, this week". The honest
                    in-progress state — not a red miss. */}
                <span className="shrink-0">
                  <Pill
                    tone={tone}
                    label={counted ? "Counted" : "Not yet"}
                  />
                </span>
              </div>

              {counted && week ? (
                <>
                  <div className="mt-1 truncate text-xs text-muted-foreground">
                    {week.submittedByName}
                    {week.submittedAt !== null
                      ? ` · ${relativeDay(week.submittedAt)}`
                      : ""}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-baseline gap-x-4 gap-y-0.5 text-sm">
                    <span className="num">
                      <b className="font-semibold">{week.itemsCounted ?? 0}</b>{" "}
                      <span className="text-muted-foreground">counted</span>
                    </span>
                    <span className="num">
                      {/* Red only when there is something to act on. A zero
                          below-floor count is the good news and must not read
                          as a warning. */}
                      <b
                        className={cn(
                          "font-semibold",
                          (week.belowFloorCount ?? 0) > 0
                            ? "text-red-700"
                            : "text-muted-foreground",
                        )}
                      >
                        {week.belowFloorCount ?? 0}
                      </b>{" "}
                      <span className="text-muted-foreground">below floor</span>
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {week.sessionId ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setOpenSession(week.sessionId)}
                      >
                        See the count
                      </Button>
                    ) : null}
                    {/* The count raised an order — but the prompt to approve it
                        is only honest while it is genuinely awaiting approval.
                        Once the manager approves (or it is dispatched, invoiced,
                        cancelled) the link stops being an action and becomes a
                        settled note. `asChild` is the registry's own answer to
                        "a link that looks like a button". */}
                    {week.topUpRequestId ? (
                      week.topUpPending ? (
                        <UiButton asChild variant="outline" size="sm">
                          <Link to={approvalsPath}>Approve the top-up</Link>
                        </UiButton>
                      ) : (
                        <Pill tone="gray" label="Top-up approved" />
                      )
                    ) : null}
                  </div>
                </>
              ) : (
                /* Not yet, this week — and the name of who to chase, which the
                   old copy hid even though the data carried it. */
                <div className="mt-1 text-xs text-muted-foreground">
                  {clinic.managerNames.length > 0
                    ? `No count yet this week — chase ${clinic.managerNames.join(", ")}.`
                    : "No count yet this week, and no manager is on file to chase."}
                </div>
              )}
            </Card>
          );
        })}
      </section>

      {history.length > 0 && anyCountEver ? (
        <History clinics={clinics} weeks={history} />
      ) : null}

      <SessionSheet
        sessionId={openSession}
        onClose={() => setOpenSession(null)}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The history strip                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Four weeks of done/missed, one row per clinic.
 *
 * A strip rather than a chart, for the reason SPEC gives: nothing is installed
 * to draw one and this screen is not the argument for installing it. Four cells
 * survive 390px, a projector and a screen reader — each cell carries its own
 * spoken sentence — and the pattern a chart would show ("this clinic misses
 * every other week") is legible from the shape of the row.
 *
 * The rows are ordered by misses, worst first, which is the same principle the
 * dashboard's by-clinic table follows: the ordering is part of the answer.
 */
type CellState = "counted" | "not-yet" | "missed" | "before-start";

function History({
  clinics,
  weeks,
}: {
  clinics: Array<{
    clinicId: Id<"clinics">;
    clinicName: string;
    weeks: Array<{ weekKey: string; state: CellState }>;
  }>;
  /** Newest first, current week already removed. */
  weeks: string[];
}) {
  const rows = clinics
    .map((clinic) => {
      const cells = weeks.map((weekKey) => ({
        weekKey,
        /* `before-start` when the clinic did not exist yet — an ineligible week,
           drawn "—" and counted in no tally. Defaulting to it (rather than to a
           miss) is the whole point: a window wider than the clinic's life must
           not read as chronic delinquency. */
        state:
          clinic.weeks.find((week) => week.weekKey === weekKey)?.state ??
          "before-start",
      }));
      /* Misses and the denominator are computed over *eligible* weeks only —
         weeks the clinic existed for. "2 of 4 tracked weeks", not "2 of 12":
         the ineligible weeks are neither missed nor tracked. */
      const eligible = cells.filter((cell) => cell.state !== "before-start");
      return {
        clinicId: clinic.clinicId,
        clinicName: clinic.clinicName,
        cells,
        tracked: eligible.length,
        missed: eligible.filter((cell) => cell.state === "missed").length,
      };
    })
    .sort(
      (a, b) => b.missed - a.missed || a.clinicName.localeCompare(b.clinicName),
    );

  /* Inline, not a Tailwind class: the number of columns is data. Tailwind's
     scanner reads class strings literally, so a `grid-cols-[…]` assembled from
     a variable produces no CSS at all. */
  const template = `minmax(0,1fr) repeat(${weeks.length}, 1.75rem)`;

  return (
    <section>
      <SubHeading className="mb-2">The weeks before</SubHeading>
      <Card className="gap-0 p-3">
        {/* The week labels, once, above every row. */}
        <div
          className="grid items-center gap-x-2 pb-1.5"
          style={{ gridTemplateColumns: template }}
        >
          <span />
          {weeks.map((weekKey) => (
            <span
              key={weekKey}
              className="num text-center text-[10px] text-muted-foreground"
            >
              {weekShort(weekKey)}
            </span>
          ))}
        </div>

        <div className="divide-y">
          {rows.map((row) => (
            <div
              key={row.clinicId}
              className="grid items-center gap-x-2 py-2"
              style={{ gridTemplateColumns: template }}
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">
                  {row.clinicName}
                </div>
                {/* The chronic-non-counter signal, in words as well as in the
                    shape of the row. The denominator is the *tracked* weeks —
                    the ones the clinic existed for — so "2 of 4" is a fact about
                    the clinic, not about how wide a window was asked for. Muted
                    when nothing is missed; a clinic too new to have a past week
                    reads "no weeks tracked yet", not a red zero. */}
                <div
                  className={cn(
                    "text-xs",
                    row.missed > 0 ? "text-red-700" : "text-muted-foreground",
                  )}
                >
                  {row.tracked === 0
                    ? "no weeks tracked yet"
                    : row.missed === 0
                      ? `none missed of ${row.tracked} tracked ${plural(row.tracked, "week")}`
                      : `${row.missed} of ${row.tracked} tracked ${plural(row.tracked, "week")} missed`}
                </div>
              </div>
              {row.cells.map((cell) => {
                /* Three renderings, not two. `counted` is the settled gray
                   Check; `missed` is a red Minus — reserved here, for a genuinely
                   past week the clinic existed for; `before-start` is a quiet
                   "—", an ineligible week that is neither a miss nor a hole.
                   Colour never carries alone: each cell also speaks its own
                   sr-only sentence. (A history strip never contains `not-yet` —
                   that is the current week, which lives on the card above.) */
                const eligible = cell.state !== "before-start";
                const spoken = !eligible
                  ? "clinic did not exist yet"
                  : cell.state === "counted"
                    ? "counted"
                    : "not counted";
                return (
                  <span
                    key={cell.weekKey}
                    title={`${row.clinicName} · ${weekLabel(cell.weekKey)} · ${spoken}`}
                    className={cn(
                      "mx-auto grid size-6 place-content-center rounded-md",
                      eligible
                        ? cn(
                            "ring-1 ring-inset",
                            TONE_CLASSES[cell.state === "counted" ? "gray" : "red"],
                          )
                        : "text-muted-foreground/50",
                    )}
                  >
                    {!eligible ? (
                      <span aria-hidden className="text-xs leading-none">
                        —
                      </span>
                    ) : cell.state === "counted" ? (
                      <Check aria-hidden className="size-3.5" />
                    ) : (
                      <Minus aria-hidden className="size-3.5" />
                    )}
                    <span className="sr-only">
                      {weekLabel(cell.weekKey)}: {spoken}
                    </span>
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* One session                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * What was actually counted, in a sheet.
 *
 * Optional depth rather than a second screen: the compliance question is
 * answered by the card, and "which items were short" is the follow-up an owner
 * asks about one clinic, once. The query is `"skip"` until a session is chosen,
 * so the screen costs nothing for the reader who never opens one.
 *
 * Below-floor lines lead, because they are the ones that raised the order.
 */
function SessionSheet({
  sessionId,
  onClose,
}: {
  sessionId: Id<"countSessions"> | null;
  onClose: () => void;
}) {
  const session = useQuery(
    api.counts.get,
    sessionId ? { sessionId } : "skip",
  );

  const lines = session
    ? [...session.lines].sort(
        (a, b) =>
          Number(b.belowFloor) - Number(a.belowFloor) ||
          a.itemName.localeCompare(b.itemName),
      )
    : [];

  return (
    <Sheet
      open={sessionId !== null}
      onClose={onClose}
      title={
        session ? `${session.clinicName} · ${weekLabel(session.weekKey)}` : "The count"
      }
      description={
        session
          ? `${session.submittedByName} · ${relativeDay(session.submittedAt)} · ${
              session.itemsCounted
            } ${plural(session.itemsCounted, "item")} counted`
          : undefined
      }
      footer={
        <Button size="lg" className="w-full" onClick={onClose}>
          Close
        </Button>
      }
    >
      {session === undefined ? (
        <div className="py-2">
          <Loading rows={3} label="Loading the count" />
        </div>
      ) : session === null ? (
        <p className="py-4 text-sm text-muted-foreground">
          That count is no longer available.
        </p>
      ) : (
        <div className="pb-2">
          <Footnote className="mb-3">
            The floor and target on each line are the ones frozen at submit —
            editing a level since then does not change what this count saw, or
            why it did or did not raise a top-up.
          </Footnote>
          <div className="divide-y">
            {lines.map((line) => (
              <div key={line._id} className="flex items-baseline gap-3 py-2">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {line.itemName}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {line.packSize}
                  </span>
                </span>
                <span className="num shrink-0 text-right text-xs">
                  <b
                    className={cn(
                      "text-sm font-semibold",
                      line.belowFloor ? "text-red-700" : "text-foreground",
                    )}
                  >
                    {line.countedQty}
                  </b>
                  <span className="block text-muted-foreground">
                    floor {line.floorAtCount} · target {line.targetAtCount}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Sheet>
  );
}
